import { useState, useEffect, useCallback, useRef } from "react";
import {
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
} from "react-flow-renderer";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { listPrompts } from "../../services/prompt.api";
import { listTags } from "../../services/tags.api";
import { availableNodes } from "../FlowBuilder/nodeCategoriesConfig";
import { autoDisponer } from "../FlowBuilder/layout/flowAutoLayout";

const useFlowEditor = ({ flowId, initialFlow, onSave, onCancel }) => {
  const [flowName, setFlowName] = useState(initialFlow?.name || "");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [saving, setSaving] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [tagsCache, setTagsCache] = useState([]);
  const [promptsCache, setPromptsCache] = useState([]);
  const initialFlowRef = useRef(null);
  const isMountedRef = useRef(true);

  // E1: isMountedRef lifecycle guard (I-5)
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // E2: Load tags for name resolution in tag/removeTag nodes
  useEffect(() => {
    const loadTags = async () => {
      try {
        const { data } = await listTags();
        const tagsArray = Array.isArray(data) ? data : (Array.isArray(data?.tags) ? data.tags : []);
        if (isMountedRef.current) {
          setTagsCache(tagsArray);
        }
      } catch (error) {
        console.error("Error cargando tags:", error);
        if (isMountedRef.current) {
          setTagsCache([]);
        }
      }
    };
    loadTags();
  }, []);

  // E3: Load prompts — unified (fixes I-8 duplicate bug)
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const { data } = await listPrompts();
        const promptsArray = Array.isArray(data?.prompts) ? data.prompts : [];
        if (isMountedRef.current) {
          setPromptsCache(promptsArray);
        }
      } catch (error) {
        console.error("Error cargando prompts:", error);
        if (isMountedRef.current) {
          setPromptsCache([]);
        }
      }
    };
    loadPrompts();
  }, []);

  // E4: Parse initialFlow JSON → ReactFlow format
  useEffect(() => {
    if (initialFlow?.flow && isMountedRef.current) {
      try {
        const flowData = typeof initialFlow.flow === "string"
          ? JSON.parse(initialFlow.flow)
          : initialFlow.flow;

        let reactFlowNodes = [];
        let reactFlowEdges = [];

        if (flowData.nodes && isMountedRef.current) {
          reactFlowNodes = flowData.nodes.map((node) => {
            const nodeData = {
              label: node.label || node.type,
              ...node.data,
            };
            return {
              id: node.id,
              type: node.type || "default",
              position: node.position || { x: 0, y: 0 },
              data: nodeData,
            };
          });
          if (isMountedRef.current) {
            setNodes(reactFlowNodes);
          }
        }

        if (flowData.edges && isMountedRef.current) {
          // ✅ CRÍTICO (I-2): Preservar sourceHandle/targetHandle para ConditionNode
          reactFlowEdges = flowData.edges.map((edge) => ({
            id: edge.id || `edge-${edge.source}-${edge.target}`,
            source: edge.source,
            target: edge.target,
            sourceHandle: edge.sourceHandle || null,
            targetHandle: edge.targetHandle || null,
            type: "buttonEdge",
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed },
          }));
          if (isMountedRef.current) {
            setEdges(reactFlowEdges);
          }
        }

        // I-4: snapshot inicial para dirty-flag
        if (isMountedRef.current) {
          initialFlowRef.current = {
            name: initialFlow.name || "",
            nodes: reactFlowNodes,
            edges: reactFlowEdges,
          };
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error("Error parsing flow data:", error);
        if (isMountedRef.current) {
          setNodes([]);
          setEdges([]);
          initialFlowRef.current = {
            name: initialFlow.name || "",
            nodes: [],
            edges: [],
          };
        }
      }
    } else if (!initialFlow && isMountedRef.current) {
      initialFlowRef.current = { name: "", nodes: [], edges: [] };
      setHasUnsavedChanges(false);
    }
  }, [initialFlow, setNodes, setEdges]);

  // E5: Patch tag names once tagsCache is loaded
  useEffect(() => {
    if (tagsCache.length === 0) return;
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        if ((node.type === "tag" || node.type === "removeTag") && node.data?.tagId && !node.data?.tagName) {
          const tag = tagsCache.find((t) => t.id === node.data.tagId);
          if (tag) {
            return { ...node, data: { ...node.data, tagName: tag.name } };
          }
        }
        return node;
      })
    );
  }, [tagsCache, setNodes]);

  // E6: Patch prompt names once promptsCache is loaded
  useEffect(() => {
    if (promptsCache.length === 0) return;
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        if (node.type === "openai" && node.data?.promptId && !node.data?.promptName) {
          const prompt = promptsCache.find((p) => p.id === node.data.promptId);
          if (prompt) {
            return { ...node, data: { ...node.data, promptName: prompt.name } };
          }
        }
        return node;
      })
    );
  }, [promptsCache, setNodes]);

  // E7: Dirty-flag detection (I-4) — JSON.stringify diff against initialFlowRef snapshot
  useEffect(() => {
    if (!initialFlowRef.current) return;
    const current = JSON.stringify({
      name: flowName,
      nodes: nodes.map((n) => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
      edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle })),
    });
    const initial = JSON.stringify({
      name: initialFlowRef.current.name,
      nodes: initialFlowRef.current.nodes.map((n) => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
      edges: initialFlowRef.current.edges.map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle })),
    });
    setHasUnsavedChanges(current !== initial);
  }, [flowName, nodes, edges]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  /**
   * Autoajuste del lienzo (0419 F3-UX). Solo reescribe `position`: las aristas y los
   * `sourceHandle` —de los que depende el ruteo del motor— no se tocan.
   *
   * No guarda: deja el flujo marcado como no guardado para que se pueda deshacer cerrando
   * sin guardar si el resultado no convence.
   */
  const handleAutoLayout = useCallback(
    (direccion = "vertical") => {
      setNodes((nds) => autoDisponer(nds, edges, direccion));
    },
    [edges, setNodes]
  );

  /**
   * `position` (0422 QA-UX): donde se soltó el nodo al arrastrarlo, o el centro de la vista al
   * hacer clic. Sin ella se cae al comportamiento antiguo —un punto al azar cerca del origen del
   * lienzo—, que en un flujo grande o desplazado dejaba el nodo nuevo fuera de la pantalla.
   */
  const handleAddNode = useCallback((nodeType, position) => {
    const nodeData = {
      label: availableNodes.find((n) => n.type === nodeType)?.label || nodeType,
    };
    if (nodeType === "content") {
      nodeData.items = [];
    }
    if (nodeType === "hsmTemplate") {
      // Config de la plantilla WABA: conexión de diseño, plantilla elegida y valores de variables.
      nodeData.whatsappId = "";
      nodeData.templateId = "";
      nodeData.templateName = "";
      nodeData.language = "";
      nodeData.variables = { header: [], body: [] };
    }
    const newNode = {
      id: `node-${Date.now()}`,
      type: nodeType,
      position: position || {
        x: Math.random() * 400 + 100,
        y: Math.random() * 400 + 100,
      },
      data: nodeData,
    };
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes]);

  const handleDuplicateNode = useCallback((nodeId) => {
    setNodes((nds) => {
      const src = nds.find((n) => n.id === nodeId);
      if (!src) return nds;
      const clone = {
        ...src,
        id: `node-${Date.now()}`,
        selected: false,
        position: { x: src.position.x + 40, y: src.position.y + 40 },
        data: JSON.parse(JSON.stringify(src.data)),
      };
      return [...nds, clone];
    });
  }, [setNodes]);

  const handleDeleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  const handleDeleteEdge = useCallback((edgeId) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
  }, [setEdges]);

  const onConnect = useCallback((params) => {
    setEdges((eds) =>
      addEdge(
        {
          ...params,
          type: "buttonEdge",
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed },
        },
        eds
      )
    );
  }, [setEdges]);

  /**
   * Reconectar una arista existente (0419 F3-UX): arrastrar uno de sus extremos a otro sitio
   * en vez de borrarla y volver a trazarla.
   *
   * No se usa el `updateEdge` de la libreria a proposito: se reescriben SOLO los cuatro campos
   * de la conexion y se conserva el resto de la arista —`type`, `animated`, `markerEnd` y el
   * propio `id`—. Con el helper habria que fiarse de que los preserve, y son justo los campos
   * que hacen que la arista se siga dibujando como las demas.
   */
  const handleEdgeUpdate = useCallback((oldEdge, newConnection) => {
    setEdges((eds) => {
      const actualizada = {
        ...oldEdge,
        source: newConnection.source,
        target: newConnection.target,
        sourceHandle: newConnection.sourceHandle ?? null,
        targetHandle: newConnection.targetHandle ?? null,
      };

      // Si la reconexion deja una arista identica a otra que ya existia, sobra: el motor solo
      // puede seguir una (`fn_flowbuilder_get_next_node` hace LIMIT 1) y la repetida no se
      // recorreria nunca. Solo se descarta el DUPLICADO EXACTO; dos salidas distintas del
      // mismo nodo son legitimas y no se tocan.
      const esLaMisma = (a, b) =>
        a.source === b.source &&
        (a.sourceHandle ?? null) === (b.sourceHandle ?? null) &&
        a.target === b.target;

      return eds
        .map((e) => (e.id === oldEdge.id ? actualizada : e))
        .filter((e, i, arr) => arr.findIndex((o) => esLaMisma(o, e)) === i);
    });
  }, [setEdges]);

  const handleNodeClick = useCallback((_event, node) => {
    setSelectedNode(node);
    setConfigModalOpen(true);
  }, []);

  const handleSaveNodeConfig = useCallback((updatedNode) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === updatedNode.id ? { ...n, data: updatedNode.data } : n))
    );
    setConfigModalOpen(false);
    setSelectedNode(null);
  }, [setNodes]);

  // I-6: onSave signature must be onSave({ id, flow, name }, false)
  const handleSave = useCallback(async () => {
    if (!flowName.trim()) {
      toast.error(i18n.t("flowbuilders.validation.nameRequired"));
      return;
    }
    try {
      if (!isMountedRef.current) return;
      setSaving(true);

      const flowNodes = nodes.map((node) => ({
        id: node.id,
        type: node.type,
        label: node.data.label,
        position: node.position,
        data: node.data,
      }));

      // ✅ CRÍTICO (I-2): preserve sourceHandle / targetHandle for ConditionNode
      const flowEdges = edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle || null,
        targetHandle: edge.targetHandle || null,
      }));

      const resultado = await onSave({ id: flowId, flow: { nodes: flowNodes, edges: flowEdges }, name: flowName }, false);

      // I-4: update snapshot so dirty-flag resets
      initialFlowRef.current = { name: flowName, nodes, edges };
      setHasUnsavedChanges(false);
      // 0422 R4: guardar deja un borrador; decirlo evita creer que ya está en producción.
      if (resultado?.estado === "borrador") {
        toast.info("Guardado como borrador. Publícalo para que lo usen las conversaciones.");
      } else {
        toast.success(i18n.t("flowbuilders.toasts.saveSuccess") || "Flujo guardado exitosamente");
      }
      return resultado || { estado: "guardado" };
    } catch (error) {
      toast.error(i18n.t("flowbuilders.toasts.saveError"));
      return null;
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  }, [flowId, flowName, nodes, edges, onSave]);

  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmDialog(true);
    } else {
      onCancel();
    }
  }, [hasUnsavedChanges, onCancel]);

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirmDialog(false);
    onCancel();
  }, [onCancel]);

  return {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    flowName,
    setFlowName,
    saving,
    configModalOpen,
    setConfigModalOpen,
    selectedNode,
    setSelectedNode,
    hasUnsavedChanges,
    showCloseConfirmDialog,
    setShowCloseConfirmDialog,
    handleAddNode,
    handleAutoLayout,
    handleDuplicateNode,
    handleDeleteNode,
    handleDeleteEdge,
    onConnect,
    handleEdgeUpdate,
    handleNodeClick,
    handleSaveNodeConfig,
    handleSave,
    handleCancel,
    handleConfirmClose,
  };
};

export default useFlowEditor;
